import { useEffect, useRef } from 'react';

const steps = [
  {
    num: '01',
    title: 'Consultation',
    desc: 'Discuss your goals, vision, and project scope. We listen first — no pressure, no sales pitch.',
    icon: 'ri-discuss-fill',
  },
  {
    num: '02',
    title: 'Planning & Estimate',
    desc: 'Clear recommendations and transparent pricing — no surprises, no hidden costs.',
    icon: 'ri-file-list-3-fill',
  },
  {
    num: '03',
    title: 'Build & Execution',
    desc: 'Skilled workmanship with attention to every detail. The owner is on site throughout.',
    icon: 'ri-tools-fill',
  },
  {
    num: '04',
    title: 'Final Walkthrough',
    desc: 'We ensure everything meets expectations before we call it done. Your satisfaction is the standard.',
    icon: 'ri-checkbox-circle-fill',
  },
];

export default function Process() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const items = entry.target.querySelectorAll('.proc-item');
            items.forEach((item, i) => {
              setTimeout(() => {
                (item as HTMLElement).style.opacity = '1';
                (item as HTMLElement).style.transform = 'translateY(0)';
              }, i * 160);
            });
          }
        });
      },
      { threshold: 0.12 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="process" className="py-32 relative overflow-hidden" style={{ background: '#141414' }}>
      {/* Background texture */}
      <div
        className="absolute inset-0 opacity-3"
        style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(201,168,124,0.3) 1px, transparent 0)',
          backgroundSize: '32px 32px',
        }}
      ></div>

      {/* Large decorative number */}
      <div
        className="absolute right-0 top-1/2 -translate-y-1/2 text-[320px] font-black leading-none select-none pointer-events-none"
        style={{
          fontFamily: 'Montserrat, sans-serif',
          color: 'rgba(201,168,124,0.03)',
          lineHeight: 1,
        }}
      >
        04
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-24">
          <div>
            <p
              className="text-[#C9A87C] text-[11px] font-bold uppercase tracking-[0.22em] mb-5"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              How It Works
            </p>
            <h2
              className="text-4xl md:text-5xl font-bold text-white leading-tight"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              A Streamlined,<br />Professional Approach
            </h2>
          </div>
          <p
            className="text-white/40 text-base leading-relaxed max-w-xs lg:mb-2"
            style={{ fontFamily: 'Lora, serif' }}
          >
            From first call to final walkthrough — a clear, structured process built around your project.
          </p>
        </div>

        {/* Steps */}
        <div ref={sectionRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 relative">
          {/* Connector line */}
          <div
            className="hidden lg:block absolute top-[56px] left-[12.5%] right-[12.5%] h-px z-0"
            style={{ background: 'linear-gradient(90deg, transparent, rgba(201,168,124,0.3), rgba(201,168,124,0.3), transparent)' }}
          ></div>

          {steps.map((step, i) => (
            <div
              key={i}
              className="proc-item relative z-10 group"
              style={{
                opacity: 0,
                transform: 'translateY(32px)',
                transition: 'opacity 0.65s ease, transform 0.65s ease',
              }}
            >
              {/* Card */}
              <div className="border border-white/6 hover:border-[#C9A87C]/30 transition-all duration-400 p-8 h-full" style={{ background: 'rgba(255,255,255,0.02)' }}>
                {/* Step number */}
                <div className="flex items-center justify-between mb-8">
                  <span
                    className="text-[#C9A87C]/25 text-5xl font-black leading-none"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    {step.num}
                  </span>
                  <div
                    className={`w-12 h-12 flex items-center justify-center transition-all duration-300 ${
                      i % 2 === 0
                        ? 'bg-[#B8956A] group-hover:bg-[#9a7a52]'
                        : 'bg-white/5 border border-[#B8956A]/40 group-hover:bg-[#B8956A]/20'
                    }`}
                  >
                    <i className={`${step.icon} text-xl ${i % 2 === 0 ? 'text-white' : 'text-[#C9A87C]'}`}></i>
                  </div>
                </div>

                <h3
                  className="text-white font-bold text-lg mb-3 group-hover:text-[#C9A87C] transition-colors duration-300"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  {step.title}
                </h3>
                <p
                  className="text-white/45 text-sm leading-relaxed"
                  style={{ fontFamily: 'Lora, serif' }}
                >
                  {step.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
